<template>
  <div class="admin-accommodation-links">
    <NavBar />
    <div class="container py-5" style="max-width: 700px; padding-top: 100px !important;">
      <h2 class="mb-4">Accommodation Link Review</h2>

      <div v-if="loading" class="text-center py-5">
        <b-spinner variant="primary" />
        <p class="mt-2">Loading accommodations...</p>
      </div>

      <div v-else-if="error" class="text-center py-5">
        <p class="text-danger">{{ error }}</p>
        <b-button variant="primary" @click="fetchAccommodations">Retry</b-button>
      </div>

      <div v-else-if="isDone" class="text-center py-5">
        <h4 class="text-success">All done!</h4>
        <p class="text-muted">{{ accommodations.length }} accommodations reviewed.</p>
      </div>

      <div v-else>
        <b-progress
          :value="currentIndex"
          :max="accommodations.length"
          class="mb-4"
          show-value
        />
        <p class="text-muted mb-4">
          {{ currentIndex + 1 }} / {{ accommodations.length }} accommodations
        </p>

        <b-card class="shadow-sm">
          <h5 class="mb-1">{{ current.name }}</h5>
          <p class="text-muted ride-name mb-1">Ride: {{ current.ride_name }} (ID: {{ current.ride_id }})</p>
          <p class="text-muted ride-name mb-1">Dates: {{ current.ride_start_date }} – {{ current.ride_end_date }}</p>
          <p class="text-muted ride-name mb-3">Owner: {{ current.owner_username }} ({{ current.owner_email }})</p>

          <div class="mb-3">
            <p class="font-weight-bold mb-1">Current link:</p>
            <a :href="current.link" target="_blank" rel="noopener" class="text-break link-text">
              {{ current.link }}
            </a>
          </div>

          <div class="mb-3">
            <p class="font-weight-bold mb-1">Affiliate link (preview):</p>
            <a :href="affiliateLink" target="_blank" rel="noopener" class="text-break link-text text-success">
              {{ affiliateLink }}
            </a>
          </div>

          <div class="mb-4">
            <p class="font-weight-bold mb-1">Link to save:</p>
            <b-form-input v-model="editableLink" />
          </div>

          <div class="d-flex flex-wrap">
            <b-button
              variant="primary"
              class="mr-2 mb-2"
              :disabled="saving"
              @click="save"
            >
              <b-spinner v-if="saving" small class="mr-1" />
              Save
            </b-button>
            <b-button variant="outline-secondary" class="mb-2" :disabled="saving" @click="skip">
              Skip
            </b-button>
          </div>

          <p v-if="saveError" class="text-danger mt-2 error-text">{{ saveError }}</p>
        </b-card>
      </div>
    </div>
  </div>
</template>

<script>
import NavBar from '@/components/NavBar';
import api from '@/api/accommodation';

const AFFILIATE_PREFIX = 'https://www.tkqlhce.com/click-101453709-17293139?url=';
const INTERNAL_HOME_PATH = '/';

export default {
  name: 'AdminAccommodationLinks',
  components: { NavBar },
  data() {
    return {
      accommodations: [],
      currentIndex: 0,
      editableLink: '',
      loading: true,
      saving: false,
      error: null,
      saveError: null,
    };
  },
  computed: {
    current() {
      return this.accommodations[this.currentIndex] || null;
    },
    affiliateLink() {
      if (!this.current || !this.current.link) return '';
      const cleanLink = this.current.link.split('?')[0];
      return AFFILIATE_PREFIX + encodeURIComponent(cleanLink);
    },
    isDone() {
      return !this.loading && this.currentIndex >= this.accommodations.length;
    },
  },
  created() {
    if (this.$store.getters.userId !== 1) {
      this.$router.push(INTERNAL_HOME_PATH);
      return;
    }
    this.fetchAccommodations();
  },
  methods: {
    async fetchAccommodations() {
      this.loading = true;
      this.error = null;
      try {
        const token = this.$store.state.appauth.apptoken;
        const response = await api.fetchPublicAccommodationsWithLinks(token);
        if (response.data.ServerMassageCode !== 'success') {
          this.error = 'Failed to load accommodations.';
          return;
        }
        this.accommodations = response.data.accommodations;
        this.prefillEditable();
      } catch (e) {
        this.error = 'Network error. Please try again.';
      } finally {
        this.loading = false;
      }
    },
    prefillEditable() {
      if (this.current) {
        const cleanLink = this.current.link.split('?')[0];
        this.editableLink = AFFILIATE_PREFIX + encodeURIComponent(cleanLink);
      }
    },
    async save() {
      this.saving = true;
      this.saveError = null;
      try {
        const token = this.$store.state.appauth.apptoken;
        const response = await api.updateAccommodationLink(token, this.current.id, this.editableLink);
        if (response.data.ServerMassageCode !== 'success') {
          this.saveError = 'Save failed. Please try again.';
          return;
        }
        this.advance();
      } catch (e) {
        this.saveError = 'Network error. Please try again.';
      } finally {
        this.saving = false;
      }
    },
    skip() {
      this.advance();
    },
    advance() {
      this.currentIndex++;
      this.saveError = null;
      this.prefillEditable();
    },
  },
};
</script>

<style scoped>
.admin-accommodation-links h2 { font-size: 28px; }
.admin-accommodation-links h4 { font-size: 24px; }
.admin-accommodation-links h5 { font-size: 20px; }
.admin-accommodation-links p,
.admin-accommodation-links .ride-name,
.admin-accommodation-links .error-text { font-size: 16px; }
.admin-accommodation-links .link-text { font-size: 14px; }
.admin-accommodation-links .btn { font-size: 16px; }
</style>
